/**
 * Global WASM cache for proxy_caller.wasm
 * Pre-fetches on app load to avoid async fetches during user interactions,
 * which is necessary for Safari/iOS popup blocker compatibility.
 */

let cachedProxyWasm: Uint8Array | null = null;
let fetchPromise: Promise<Uint8Array> | null = null;
let fetchError: Error | null = null;

/**
 * Initialize the WASM cache by fetching the proxy_caller.wasm.
 * Call this early in app startup (e.g., in App.tsx useEffect).
 * Safe to call multiple times - will only fetch once.
 */
export const initProxyWasmCache = async (): Promise<void> => {
  if (cachedProxyWasm !== null) {
    return; // Already cached
  }

  if (fetchPromise !== null) {
    await fetchPromise; // Wait for in-progress fetch

    return;
  }

  fetchPromise = fetchProxyWasm();

  try {
    cachedProxyWasm = await fetchPromise;
    fetchError = null;
  } catch (error) {
    fetchError = error instanceof Error ? error : new Error('Failed to fetch proxy WASM');
    throw fetchError;
  } finally {
    fetchPromise = null;
  }
};

/**
 * Get the cached proxy WASM bytes.
 * Returns immediately if cached, otherwise waits for fetch to complete.
 * Throws if WASM failed to load.
 */
export const getProxyWasmCached = async (): Promise<Uint8Array> => {
  if (cachedProxyWasm !== null) {
    return cachedProxyWasm;
  }

  if (fetchError !== null) {
    throw fetchError;
  }

  if (fetchPromise !== null) {
    return await fetchPromise;
  }

  // Cache not initialized yet - initialize now
  await initProxyWasmCache();

  if (cachedProxyWasm === null) {
    throw new Error('Proxy WASM failed to load');
  }

  return cachedProxyWasm;
};

/**
 * Synchronously get the cached proxy WASM bytes.
 * Returns null if not yet cached.
 * Use this for Safari compatibility where you need synchronous access.
 */
export const getProxyWasmSync = (): Uint8Array | null => {
  return cachedProxyWasm;
};

/**
 * Check if the proxy WASM is cached and ready.
 */
export const isProxyWasmCached = (): boolean => {
  return cachedProxyWasm !== null;
};

/**
 * Internal fetch function - fetches from the public folder
 */
const fetchProxyWasm = async (): Promise<Uint8Array> => {
  const result = await fetch('/proxy_caller.wasm');
  if (!result.ok) {
    throw new Error(`Failed to fetch proxy WASM: ${result.status} ${result.statusText}`);
  }
  const buffer = await result.arrayBuffer();

  return new Uint8Array(buffer);
};
